import Editor from "@monaco-editor/react";
import { Box, Link, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiEdit } from "react-icons/fi";
import { toast } from "react-toastify";

import { useUpdateCalcPanel } from "../hooks/useFetchData.hook";
import { controlJSON } from "../utils/jsonUtils";

const EditorComponent = ({
    elementData,
    onContentChange,
}: {
    elementData: any;
    onContentChange: (content: string) => void;
}) => {
    const editorRef = useRef<any>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [editing, setEditing] = useState<boolean>(false);
    const { updateCalcPanel, updateLoading } = useUpdateCalcPanel();

    // Reset success state when updateLoading changes to false (operation completed)
    useEffect(() => {
        if (!updateLoading && success) {
            const timer = setTimeout(() => {
                setEditing(false);
                setSuccess(false);
                setLoading(false);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [updateLoading, success]);

    const handleEditorDidMount = useCallback((editor: any) => {
        editorRef.current = editor;
    }, []);

    const handleCancelClick = useCallback(() => {
        setEditing(false);
    }, []);

    const refreshButtonData = useCallback(() => {
        setError(false);
        setLoading(false);
        setSuccess(false);
    }, []);

    const handleSaveClickJSON = useCallback(async () => {
        setLoading(true);
        setSuccess(false);
        setError(false);
        const newValueJson = editorRef?.current?.getValue();
        const refreshJsonDataControl = controlJSON(newValueJson);

        if (refreshJsonDataControl) {
            try {
                await updateCalcPanel({
                    url: `https://${elementData?.data?.url}`,
                    data: {
                        content: newValueJson,
                    },
                });
            } catch (error) {
                setError(true);
                setLoading(false);
                console.error(error);
            } finally {
                setSuccess(true);
                setLoading(false);
                toast.success("Content updated successfully");
                setTimeout(() => {
                    setEditing(false);
                    setSuccess(false);
                    setLoading(false);
                }, 1000);
            }
        } else {
            setError(true);
            setLoading(false);
            toast.error("Invalid JSON");
        }
    }, [elementData?.data?.url, updateCalcPanel]);

    const dataJSON = useMemo(
        () => (elementData?.data?.content ? JSON.parse(elementData.data.content) : null),
        [elementData?.data?.content]
    );

    const dataJSONString = useMemo(() => (dataJSON ? `${JSON.stringify(dataJSON, null, 4)}` : ""), [dataJSON]);

    const isReadOnly = useMemo(() => elementData?.data?.activated && !editing, [elementData?.data?.activated, editing]);

    return (
        <Box className="formElement">
            <Box className="formElement-child-editor">
                <Box className="elementForm" display="flex" alignItems="center" justifyContent="space-between">
                    <Typography component="label" htmlFor="editor">
                        Content (JSON)
                    </Typography>
                    {elementData?.data?.activated &&
                        (editing ? (
                            <Box className="saveCancel" display="flex" alignItems="center" gap={2}>
                                <Link component="button" onClick={handleCancelClick}>
                                    Cancel
                                </Link>
                                <Link component="button" onClick={handleSaveClickJSON}>
                                    {!success ? (
                                        error ? (
                                            <Typography color="error">Error</Typography>
                                        ) : loading || updateLoading ? (
                                            <Typography color="info">Saving...</Typography>
                                        ) : (
                                            <>Save</>
                                        )
                                    ) : (
                                        <Typography color="success">Saved</Typography>
                                    )}
                                </Link>
                            </Box>
                        ) : (
                            <Typography component="p" onClick={() => setEditing(true)} sx={{ cursor: "pointer" }}>
                                <FiEdit />
                            </Typography>
                        ))}
                </Box>
                <Editor
                    onChange={(value) => {
                        onContentChange(value);
                        refreshButtonData();
                    }}
                    options={{
                        readOnly: isReadOnly,
                    }}
                    height="40vh"
                    defaultLanguage="json"
                    defaultValue={dataJSONString}
                    theme="vs-dark"
                    onMount={handleEditorDidMount}
                />
            </Box>
        </Box>
    );
};

export default EditorComponent;
